import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ActionIcon, Button, Group, Tooltip } from "@mantine/core";
import { useDisclosure, useDocumentTitle } from "@mantine/hooks";
import { IconPlus, IconRefresh } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { StringParam, useQueryParam, withDefault } from "use-query-params";
import { useShallow } from "zustand/react/shallow";

import { StyledLaboratory } from "@pages/Patients/Laboratory/Laboratory.styled";

import { WithPatientNavbar } from "@layouts/WithPatientNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { TabsContent, TabsList, TabsRoot } from "@components/Tabs";

import { PAGE_TITLE, QP } from "@constants/index";

import { immunizationQKeys } from "@hooks/queries/immunization/queryKeys";
import { useAppStore } from "@hooks/store/useAppStore";
import { useRefreshAnimation } from "@hooks/useRefreshAnimation";

import { AddModal } from "./components/AddModal";
import { FastTab } from "./components/FastTab";
import { GeneralTab } from "./components/GeneralTab";


export const Immunization = () => {
  const { t } = useTranslation();
  useDocumentTitle(`${t("immunization")} • ${PAGE_TITLE}`);
  const queryClient = useQueryClient();
  const { patientId } = useParams();

  const [storeTabs, setStoreTabs] = useAppStore(
    useShallow(state => [state.tabs, state.setTabs])
  );
  const [currentTab, setCurrentTab] = useQueryParam(
    QP.TAB,
    withDefault(StringParam, storeTabs.immunizationTab)
  );

  const [opened, { open, close }] = useDisclosure(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { buttonRef } = useRefreshAnimation({ isRefreshing });

  useEffect(() => {
    if (currentTab) setCurrentTab(currentTab, "replaceIn");
  }, []);

  return (
    <WithSidebar hasPatientNavbar>
      <WithPatientNavbar>
        <StyledLaboratory>
          <TabsRoot
            value={currentTab as string}
            onValueChange={val => {
              setCurrentTab(val, "replace");
              setStoreTabs({ immunizationTab: val });
            }}>
            <div className="header">
              <AddModal opened={opened} close={close} />
              <Group gap={"md"}>
                <TabsList
                  ariaLabel="Immunization"
                  comboboxItems={[
                    { label: t("general"), value: "general" },
                    {
                      label: t("fast-consultation-files"),
                      value: "fast",
                    },
                  ]}
                />
                <Tooltip
                  label={t("refresh-data")}
                  position="right-end"
                  fz={12}
                  events={{ focus: true, hover: true, touch: true }}>
                  <ActionIcon
                    ref={buttonRef}
                    variant={"default"}
                    size={"md"}
                    onClick={() => {
                      queryClient.invalidateQueries({
                        queryKey: immunizationQKeys.patient(
                          patientId as string
                        ),
                      });
                    }}>
                    <IconRefresh
                      className={"refresh-icon"}
                      color="var(--mantine-color-black)"
                      size={16}
                    />
                  </ActionIcon>
                </Tooltip>
              </Group>

              <PermissionGate permission="preventative.create"><Button leftSection={<IconPlus size={18} />} onClick={open}>
                {t("add-immunization")}
              </Button></PermissionGate>
            </div>

            <TabsContent value="general">
              <GeneralTab setIsRefreshing={setIsRefreshing} />
            </TabsContent>
            <TabsContent value="fast">
              <FastTab setIsRefreshing={setIsRefreshing} />
            </TabsContent>
          </TabsRoot>
        </StyledLaboratory>
      </WithPatientNavbar>
    </WithSidebar>
  );
};
